<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div
      v-if="accountCreationSuccessCount.length || accountCreationFailed.length"
      class="m-2"
    >
      <b-row>
        <b-col
          v-if="accountCreationSuccessCount"
          md="6"
          class="mb-2"
        >
          <b-alert
            show
            variant="primary"
            class="p-2"
          >
            {{ accountCreationSuccessCount }}
          </b-alert>
        </b-col>
        <b-col
          v-if="accountCreationFailed.length"
          md="6"
          class="mb-2"
        >
          <div>
            <b-button variant="danger">
              {{ $t('FAILED LISTS') }}
            </b-button>
          </div>
          <b-table
            ref="accountCreationFailedReport"
            :items="accountCreationFailed"
            responsive
            :fields="failedHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          /></b-col>
      </b-row>
    </div>
    <div class="m-2">
      <b-row class="d-flex align-items-center">
        <b-col md="6">
          <validation-observer ref="batchAccountRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select file (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col
          md="6"
        >
          <b-row>
            <b-col md="5">
              <Button
                v-if="getUserPermission('BATCH_ACCOUNT_UPLOAD') && parseExcelFile.length"
                :label="$t('Next >')"
                size="md"
                :hidden="isNextValidateHidden"
                :loading="isLoading"
                @click="handleNextValidate"
              />
              <Button
                v-if="getUserPermission('BATCH_ACCOUNT_UPLOAD') && !errorMessages.length"
                label="Create Batch Accounts"
                size="md"
                :hidden="isCreateBatchHidden"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="handleCreate"
              />
            </b-col>
            <b-col md="7">
              <Button
                class="ml-lg-2"
                align="end"
                :label="$t('Download Batch Account Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <b-table
      v-if="errorMessages.length"
      ref="accountCreationSuccessCountReport"
      :items="errorMessages"
      responsive
      :fields="translatedErrorHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    />
    <p
      v-if="totalSheetRows"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BTable, BFormFile, BButton, BAlert,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
// import Papa from 'papaparse'
import XLSX from 'xlsx'
import Button from '../../components/button/Button.vue'
import { toKobo } from '@/helpers/formatAmount'
import { batchAccountFormat, tableLessHeaders, tableAllHeaders } from '../../../helpers/data'
import { toYearWithDate } from '../../../helpers/formatDate'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BFormFile,
    ValidationObserver,
    Button,
    BButton,
    BAlert,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      totalSheetRows: '',
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      validatedExcelFileData: [],
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableLessHeaders,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'oldCustomerID', sortable: true, label: 'Old Customer ID' },
        { key: 'customerType', sortable: true, label: 'Customer Type' },
        { key: 'title', sortable: true, label: 'Title' },
        { key: 'lastName', sortable: true, label: 'Last Name' },
        { key: 'firstName', sortable: true, label: 'First Name' },
        { key: 'otherNames', sortable: true, label: 'Other Names' },
        { key: 'gender', sortable: true, label: 'Gender' },
        { key: 'address', sortable: true, label: 'Address' },
        { key: 'maritalStatus', sortable: true, label: 'Marital Status' },
        { key: 'maidenName', sortable: true, label: 'Maiden Name' },
        { key: 'religion', sortable: true, label: 'Religion' },
        { key: 'signatory', label: 'Signatory' },
        { key: 'mandateRule', label: 'Mandate Rule' },
        { key: 'phoneNumber', sortable: true, label: 'Phone Number' },
        { key: 'email', sortable: true, label: 'Email' },
        { key: 'hometown', sortable: true, label: 'Home Town' },
        { key: 'dateOfBirth', sortable: true, label: 'Date Of Birth' },
        { key: 'nationality', sortable: true, label: 'Nationality' },
        { key: 'state', sortable: true, label: 'State' },
        { key: 'lga', sortable: true, label: 'LGA' },
        { key: 'meansOfIdentification', sortable: true, label: 'Means Of Identification' },
        { key: 'meansOfIdentificationNumber', sortable: true, label: 'Means Of Identification Number' },
        { key: 'nokName', sortable: true, label: 'Next Of Kin Name' },
        { key: 'nokAddress', sortable: true, label: 'Next Of Kin Address' },
        { key: 'nokGender', sortable: true, label: 'Next Of Kin Gender' },
        { key: 'nokRelationship', sortable: true, label: 'Next Of Kin Relationship' },
        { key: 'nokPhoneNumber', sortable: true, label: 'Next Of Kin Phone Number' },
        { key: 'nokEmail', sortable: true, label: 'Next Of Kin Email' },
        { key: 'occupation', sortable: true, label: 'Occupation' },
        { key: 'employerName', sortable: true, label: 'Employer Name' },
        { key: 'employerAddress', sortable: true, label: 'Employer Address' },
        { key: 'employerPhoneNumber', sortable: true, label: 'Employer Phone Number' },
        { key: 'referenceAccountNumber', sortable: true, label: 'Reference Account Number' },
        { key: 'referenceNUBAN', sortable: true, label: 'Reference NUBAN' },
        { key: 'bankVerificationNumber', sortable: true, label: 'Bank Verification Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountType', sortable: true, label: 'Account Type' },
        { key: 'accountStatus', sortable: true, label: 'Account Status' },
        { key: 'branchCode', sortable: true, label: 'Branch Code' },
        { key: 'productCode', sortable: true, label: 'Product Code' },
        { key: 'accountOfficerStaffID', sortable: true, label: 'Account Officer Staff ID' },
        { key: 'accountTierLevel', sortable: true, label: 'Account Tier Level' },
        { key: 'accessLevel', sortable: true, label: 'Access Level' },
        { key: 'enableEmailNotification', sortable: true, label: 'Enable Email Notification' },
        { key: 'enableSMSNotification', sortable: true, label: 'Enable SMS Notification' },
        { key: 'statementDeliveryMode', sortable: true, label: 'Statement Delivery Mode' },
        { key: 'statementDeliveryFrequency', sortable: true, label: 'Statement Delivery Frequency' },
        { key: 'minimumBalanceRequired', sortable: true, label: 'Minimum Balance Required' },
        { key: 'referee1CustomerID', sortable: true, label: 'Referee 1 Customer ID' },
        { key: 'referee2CustomerID', sortable: true, label: 'Referee 2 Customer ID' },
      ],
      required,
      isDisabled: true,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      isCreateBatchHidden: true,
      isNextValidateHidden: false,
      accountCreationSuccessCount: [],
      accountCreationFailed: [],
      sortDesc: false,
      sortBy: 'lastName',
      errorHeaders: [
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
      errorMessages: [],
    }
  },
  computed: {
    translatedErrorHeaders() {
      return this.errorHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      batchAccountCreation: 'batch/batchAccountCreation',
      downloadAccountFormat: 'batch/downloadAccountFormat',
      validateBatchAccountCreation: 'batch/validateBatchAccountCreation',
    }),
    async handleCreate() {
      this.$refs.batchAccountRules.validate().then(success => {
        if (success) {
          this.confirm(this.batchAccountCreationAction, { text: this.$t('Are you sure you want to Create Batch Accounts?') })
        }
      })
    },
    async batchAccountCreationAction() {
      try {
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          gender: item.gender ? Number(item.gender) : null,
          email: item.email ? item.email : null,
          phoneNumber: item.phoneNumber ? item.phoneNumber : null,
          meansOfIdentificationNumber: item.meansOfIdentificationNumber ? item.meansOfIdentificationNumber : null,
          maritalStatus: item.maritalStatus ? Number(item.maritalStatus) : null,
          religion: item.religion ? Number(item.religion) : null,
          dateOfBirth: item.dateOfBirth ? toYearWithDate(item.dateOfBirth) : null,
          meansOfIdentification: item.meansOfIdentification ? Number(item.meansOfIdentification) : null,
          nokGender: item.nokGender ? Number(item.nokGender) : null,
          accountType: item.accountType ? Number(item.accountType) : null,
          accountStatus: item.accountStatus ? Number(item.accountStatus) : null,
          accountTierLevel: item.accountTierLevel ? Number(item.accountTierLevel) : 1,
          accessLevel: item.accessLevel ? Number(item.accessLevel) : null,
          enableEmailNotification: item.enableEmailNotification ? Boolean(item.enableEmailNotification) : false,
          enableSMSNotification: item.enableSMSNotification ? Boolean(item.enableSMSNotification) : false,
          statementDeliveryMode: item.statementDeliveryMode ? Number(item.statementDeliveryMode) : null,
          statementDeliveryFrequency: item.statementDeliveryFrequency ? Number(item.statementDeliveryFrequency) : null,
          minimumBalanceRequired: item.minimumBalanceRequired ? toKobo(item.minimumBalanceRequired) : null,
        }))
        const response = await this.batchAccountCreation({ payload: formData })
        const { status, data } = response
        if (status === 200) {
          this.hasFileData = []
          this.parseExcelFile = []
          const text = this.$t('Batch Customer Accounts was successfully created')
          this.accountCreationSuccessCount = `(${data.successCount}) ${text}`
          this.accountCreationFailed = data.failed
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const errorText = this.$t('Your request to create Batch Accounts failed. Reason')
          this.alertError({ text: `${errorText}: ${responseError.errorMessage}` })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.errorMessages = []
      this.isCreateBatchHidden = true
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary', cellDates: true, dateNF: 'mm/dd/yyyy;@' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false }).map(row => Object.keys(row).reduce((obj, key) => {
            // eslint-disable-next-line no-param-reassign
            obj[key.trim()] = row[key]
            return obj
          }, {}))
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            oldCustomerID: items['Old Customer ID'],
            customerType: items['Customer Type'],
            title: items.Title,
            lastName: items['Last Name'],
            firstName: items['First Name'],
            otherNames: items['Other Names'],
            gender: items.Gender,
            address: items.Address,
            maritalStatus: items['Marital Status'],
            maidenName: items['Maiden Name'],
            religion: items.Religion,
            signatory: items.Signatory,
            mandateRule: items['Mandate Rule'],
            phoneNumber: items['Phone Number'],
            email: items.Email,
            homeTown: items['Home Town'],
            dateOfBirth: items['Date Of Birth'],
            nationality: items.Nationality,
            state: items.State,
            lga: items.LGA,
            meansOfIdentification: items['Means Of Identification'],
            meansOfIdentificationNumber: items['Means Of Identification Number'],
            nokName: items['Next Of Kin Name'],
            nokAddress: items['Next Of Kin Address'],
            nokGender: items['Next Of Kin Gender'],
            nokRelationship: items['Next Of Kin Relationship'],
            nokPhoneNumber: items['Next Of Kin Phone Number'],
            nokEmail: items['Next Of Kin Email'],
            occupation: items.Occupation,
            employerName: items['Employer Name'],
            employerAddress: items['Employer Address'],
            employerPhoneNumber: items['Employer Phone Number'],
            referenceAccountNumber: items['Reference Account Number'],
            referenceNUBAN: items['Reference NUBAN'],
            bankVerificationNumber: items['Bank Verification Number'],
            accountName: items['Account Name'],
            accountType: items['Account Type'],
            accountStatus: items['Account Status'],
            branchCode: items['Branch Code'],
            productCode: items['Product Code'],
            accountOfficerStaffID: items['Account Officer Staff ID'],
            accountTierLevel: items['Account Tier Level'],
            accessLevel: items['Access Level'],
            enableEmailNotification: items['Enable Email Notification'],
            enableSMSNotification: items['Enable SMS Notification'],
            statementDeliveryMode: items['Statement Delivery Mode'],
            statementDeliveryFrequency: items['Statement Delivery Frequency'],
            minimumBalanceRequired: items['Minimum Balance Required'],
            referee1CustomerID: items['Referee1 Customer ID'],
            referee2CustomerID: items['Referee2 Customer ID'],
          }))
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    async handleNextValidate() {
      try {
        this.hasFileData = []
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          customerType: item.customerType ? Number(item.customerType) : null,
          email: item.email ? item.email : null,
          phoneNumber: item.phoneNumber ? item.phoneNumber : null,
          meansOfIdentificationNumber: item.meansOfIdentificationNumber ? item.meansOfIdentificationNumber : null,
          gender: item.gender ? Number(item.gender) : null,
          maritalStatus: item.maritalStatus ? Number(item.maritalStatus) : null,
          religion: item.religion ? Number(item.religion) : null,
          dateOfBirth: item.dateOfBirth ? toYearWithDate(item.dateOfBirth) : null,
          meansOfIdentification: item.meansOfIdentification ? Number(item.meansOfIdentification) : null,
          nokGender: item.nokGender ? Number(item.nokGender) : null,
          accountType: item.accountType ? Number(item.accountType) : null,
          accountStatus: item.accountStatus ? Number(item.accountStatus) : null,
          accountTierLevel: item.accountTierLevel ? Number(item.accountTierLevel) : 1,
          accessLevel: item.accessLevel ? Number(item.accessLevel) : null,
          enableEmailNotification: item.enableEmailNotification ? Boolean(item.enableEmailNotification) : false,
          enableSMSNotification: item.enableSMSNotification ? Boolean(item.enableSMSNotification) : false,
          statementDeliveryMode: item.statementDeliveryMode ? Number(item.statementDeliveryMode) : null,
          statementDeliveryFrequency: item.statementDeliveryFrequency ? Number(item.statementDeliveryFrequency) : null,
          minimumBalanceRequired: item.minimumBalanceRequired ? toKobo(item.minimumBalanceRequired) : null,
        }))
        const response = await this.validateBatchAccountCreation({ payload: formData })
        const { status, data } = response
        if (status === 200) {
          this.errorMessages = data.failed
          this.isCreateBatchHidden = false
          if (this.errorMessages.length) {
            this.isNextValidateHidden = false
          } else {
            this.isNextValidateHidden = true
            this.alertSuccess({ text: this.$t('Everything seems good! Continue with Batch Account Creation') })
          }
          this.isDisabled = false
        }
      } catch (error) {
        // this.handleError(error)
        // this.errorMessages = error.response.data.errors
        this.isCreateBatchHidden = true
        this.isDisabled = true
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Batch Account Creation Format?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        // const BatchAccountFileTemplate = 1
        const BatchAccountFileTemplate = 15
        const response = await this.downloadAccountFormat(BatchAccountFileTemplate)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Batch-Account-Creation.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$t('Your request to download Batch Account Creation Format was successful') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Your request to download Batch Account Creation Format failed. Reason')
          this.alertError({ text: `${reasonText}: ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
